import { FC, useCallback, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Flex, Icon, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { BiCloudUpload } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';

import { FileActivityPanel } from '../../components/file-activity-panel/file-activity-panel';
import * as FileBrowser from '../../components/file-browser';
import { EmptyState } from '../../components/file-browser/empty-state';
import { Pagination } from '../../components/pagination/pagination';
import * as ToastConfigs from '../../configs/toast';
import { DIRECTORY_ATTRIBUTES } from '../../constants/directory';

import { isFile } from '../../graphql/utils/utils';
import { useCreateFolder } from '../../hooks/use-create-folder/use-create-folder';
import { useFileUpload } from '../../hooks/use-file-upload/use-file-upload';
import { UsePaginationBag } from '../../hooks/use-pagination/use-pagination';
import { useRestFileDownload } from '../../hooks/use-rest-file-download';
import { DebounceLoadingState } from '../../ui/debounce-loading/debounce-loading';
import { DropOverlay } from '../../ui/drop-overlay/drop-overlay';
import { enhanceFiles, removeIgnoredFiles } from '../../utils/file/file';
import { DirectoryErrorState } from './directory-error-state';
import { navigateToDirectory } from './file-browser-page';
import { useSelectedFiles } from './use-selected-files';

type Props = {
  pathId?: string;
  cedantId: string;
  directoryId?: string;
  canCreate: boolean;
  loading: boolean;
  error?: ApolloError;
  pagination: UsePaginationBag;
  refetchFiles: () => Promise<void>;
  pausePolling: () => void;
  resumePolling: () => void;
};

export const FileBrowserContainer: FC<Props> = ({
  error,
  refetchFiles,
  pathId,
  loading,
  cedantId,
  directoryId,
  pagination,
  canCreate,
  pausePolling,
  resumePolling,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const activityFeed = useDisclosure();
  const { showFolderDialog } = useCreateFolder();
  const { upload, isUploading } = useFileUpload();
  const { downloadFile, downloadFolder } = useRestFileDownload();

  const files = pagination.page.filter(
    (object) => object.__typename === 'File'
  );

  const [isShowingMultiSelect, setIsShowingMultiSelect] = useState(false);
  const [isDownloadingFolder, setIsDownloadingFolder] = useState(false);
  const {
    selectedFiles,
    toggleFileSelection,
    selectAllFiles,
    clearAllFiles,
  } = useSelectedFiles(files);

  const handleOpenActivityFeed = () => activityFeed.onOpen();

  const handleDownloadFolder = () => {
    if (!directoryId) {
      toast(ToastConfigs.problemCompletingRequest);
      return;
    }

    const folderName = pathId ? decodeURIComponent(pathId).split('/').pop() || 'download' : 'download';
    
    setIsDownloadingFolder(true);
    downloadFolder({
      cedantName: cedantId,
      directoryId,
      zipFileName: `${folderName}.zip`,
    }).catch(() => {
      toast(ToastConfigs.problemCompletingRequest);
    }).finally(() => {
      setIsDownloadingFolder(false);
    });
  };

  const handleFileDownload = (storageObject: { cedant: string; id: string; name: string; __typename: string; }) => {
    if (!isFile(storageObject)) return;
    
    downloadFile({
      cedantName: storageObject.cedant,
      id: storageObject.id,
      actualFileName: storageObject.name,
    }).catch(() => {
      toast(ToastConfigs.problemCompletingRequest);
    });
  };

  const onUploadComplete = useCallback(() => {
    if (pagination.currentPageNumber !== 1) {
      navigate(navigateToDirectory({ cedantId, directoryId }, { page: '1' }));
      return;
    }
    
    if (!refetchFiles) {
      return;
    }
    
    refetchFiles()?.catch(() => {
      toast(ToastConfigs.problemCompletingRequest);
    });
  }, [
    cedantId,
    navigate,
    pagination.currentPageNumber,
    directoryId,
    refetchFiles,
  ]);

  const handleUpload = useCallback(
    (files: FileWithPath[]) => {
      const supportedFiles = removeIgnoredFiles(files);
      const enhancedFiles = enhanceFiles(supportedFiles);

      upload({
        pathId,
        cedantId,
        onUploadComplete,
        files: enhancedFiles,
      });
    },
    [cedantId, onUploadComplete, pathId, upload]
  );

  const handleCreateFolder = () => showFolderDialog({ cedantId, pathId });

  const setShowMultiSelect = (showMultiSelect: boolean) => {
    if (showMultiSelect) {
      pausePolling();
    } else {
      resumePolling();
    }
    setIsShowingMultiSelect(showMultiSelect);
  };

  const toggleMultiSelect = () => {
    setShowMultiSelect(!isShowingMultiSelect);
    clearAllFiles();
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    inputRef,
  } = useDropzone({
    onDrop: handleUpload,
    noClick: true,
    disabled: isUploading || !canCreate,
    useFsAccessApi: false,
  });

  const handleUploadFolder = () => {
    DIRECTORY_ATTRIBUTES.forEach((attr) =>
      inputRef.current?.setAttribute(attr, '')
    );
    open();
  };
  const handleUploadFile = () => {
    DIRECTORY_ATTRIBUTES.forEach((attr) =>
      inputRef.current?.removeAttribute(attr)
    );
    open();
  };

  if (error) {
    return <DirectoryErrorState error={error} />;
  }

  if (loading) {
    return (
      <DebounceLoadingState>
        <FileBrowser.LoadingState />
      </DebounceLoadingState>
    );
  }

  return (
    <Flex
      flex={1}
      outline="none"
      data-testid="file-browser-page"
      {...getRootProps()}
    >
      <DropOverlay
        disabled={isUploading}
        isDragActive={isDragActive}
        data-testid="upload-drop-overlay"
      >
        <Flex direction="column" align="center">
          <Text fontSize="2xl" fontWeight="600">
            Drop file(s) here to upload them
          </Text>
          <Icon as={BiCloudUpload} fontSize="9rem" />
        </Flex>
        <input {...getInputProps()} />
      </DropOverlay>
      <Flex flex={1} direction="column">
        <FileBrowser.Toolbar
          cedantId={cedantId}
          pagination={pagination}
          isUploading={isUploading}
          isDownloadingFolder={isDownloadingFolder}
          onUploadFile={handleUploadFile}
          onCreateFolder={handleCreateFolder}
          onDownloadFolder={handleDownloadFolder}
          onUploadFolder={handleUploadFolder}
          onOpenActivityFeed={handleOpenActivityFeed}
          canCreate={canCreate}
          isMultiSelectEnabled={isShowingMultiSelect}
          toggleMultiSelect={toggleMultiSelect}
          selectedFiles={selectedFiles}
          selectAllFiles={selectAllFiles}
          clearAllFiles={clearAllFiles}
        />
        <FileBrowser.FileBrowser
          objects={pagination.page}
          data-testid="file-browser"
          onDownload={handleFileDownload}
          isShowingMultiSelect={isShowingMultiSelect}
          selectedFiles={selectedFiles}
          toggleFileSelection={toggleFileSelection}
          selectAllFiles={selectAllFiles}
          clearAllFiles={clearAllFiles}
          EmptyState={
            <EmptyState
              title="This folder is empty"
              subtitle="Files will appear here once they have been uploaded"
            />
          }
        />
        <FileActivityPanel {...activityFeed} cedant={cedantId} />
      </Flex>
      {pagination.totalItemCount > 0 && (
        <Pagination
          pageSize={pagination.pageSize}
          total={pagination.totalItemCount}
          current={pagination.currentPageNumber}
          onChange={pagination.handlePageChange}
          data-testid="file-browser-pagination"
        />
      )}
    </Flex>
  );
};
